import { getAuthCookie } from '@/utilities/cookies';
import { useMount } from '@simplywallst/ui-core';

interface Props {
  onRedirect: () => void;
  onUnauthenticated?: () => void;
}

/**
 * A hook that checks for an authentication cookie on mount, and calls a specified function
 * if the cookie is present, and another specified function if the cookie is not present.
 *
 * @param {object} props - The props for the hook.
 * @param {function} props.onRedirect - The function to call if the cookie is present and the user
 * should be redirected.
 * @param {function} [props.onUnauthenticated] - The function to call if the cookie is not present
 * or not valid.
 *
 * @example
 *
 * function MyComponent() {
 *   const dispatch = useDispatch();
 *
 *   useAuthenticationRedirect({
 *     onRedirect: () => {
 *       dispatch(redirect({ type: ROUTE_DASHBOARD }));
 *     },
 *     onUnauthenticated: () => {
 *       trackEvent({action: 'landed'})
 *     },
 *   });
 *
 *   return (
 *     <div>
 *       My component content
 *     </div>
 *   );
 * }
 */
export function useAuthenticationRedirect({
  onRedirect,
  onUnauthenticated,
}: Props) {
  useMount(() => {
    async function checkCookie() {
      const cookie = await getAuthCookie();
      if (
        typeof cookie !== 'undefined' &&
        typeof cookie.accessToken === 'string' &&
        cookie.accessToken !== ''
      ) {
        onRedirect();
      } else {
        onUnauthenticated?.();
      }
    }
    checkCookie();
  });
}
